// frontend/src/App.js
// App.js - Mobile-First Design with responsive components

import React, { useState, useEffect, useRef, lazy, Suspense } from 'react';
import { BrowserRouter, Routes, Route } from 'react-router-dom';

import axios from 'axios';
import { Capacitor } from '@capacitor/core';
import { SplashScreen } from '@capacitor/splash-screen';

// Mark the document so iOS-native-only CSS rules can target it
if (Capacitor.isNativePlatform() && Capacitor.getPlatform() === 'ios') {
  document.documentElement.classList.add('ios-native');
}
import { AuthProvider, useAuth } from './hooks/useAuth';
import { ToastProvider, useToast } from './hooks/useToast';
import Login from './components/Login';
import GenotypeManagement from './components/GenotypeManagement';
import CageManagement from './components/CageManagement';
import LitterManagement from './components/LitterManagement';
import SettingsManagement from './components/SettingsManagement';
import Register from './components/Register';
import ResetPassword from './components/ResetPassword';
import { VerifyEmail } from './components/VerifyEmail';
import ForgotPassword from './components/ForgotPassword';
const LandingPage = lazy(() => import('./components/LandingPage'));
import MiceManagement from './components/MiceManagement';
import Dashboard from './components/Dashboard';
import Welcome from './components/Welcome';
import NotFound from './components/NotFound';
import { useNoIndex } from './hooks/useNoIndex';
import FeedbackButton from './components/FeedbackButton';
import { DataService } from './services/DataService';
import { StorageService } from './services/StorageService';
import { useSwipeNavigation } from './hooks/useSwipeNavigation';
import { useKeyboardScroll } from './hooks/useKeyboardScroll';
import { useIdleSync } from './hooks/useIdleSync';
import './App.css';

const ITEMS_PER_PAGE_OPTIONS = [10, 25, 50, 100];

// Paths that only exist because reminder emails point at them. Kept in step
// with the entity_type CHECK constraint on email_reminder_rules ('mice',
// 'cages', 'litters') — adding a value there means adding it here, or that
// reminder's link starts landing on the 404 page.
const EMAIL_DEEP_LINK_PATHS = ['/mice', '/cages', '/litters'];

// Renders its children unchanged, with the page marked noindex.
function NoIndex({ children }) {
  useNoIndex();
  return children;
}


// Navigation component with mobile-first design
function Navigation({ activeView, setActiveView, isMobile }) {
 const handleTabClick = (view) => {
   if (activeView === view) {
     // Already on this tab — reset filters/search and scroll to top
     window.dispatchEvent(new CustomEvent('squeakbase-tab-reset', { detail: { view } }));
     const wrapper = document.querySelector('.swipe-content-wrapper');
     if (wrapper) wrapper.scrollTop = 0;
   } else {
     setActiveView(view);
   }
 };

 return (
  <nav className="navigation-bar">
    <button onClick={() => handleTabClick('dashboard')} className={`${activeView === 'dashboard' ? 'active' : ''} home-nav-btn`} data-testid="dashboard-nav-button">
        {isMobile ? (
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="home-icon-svg">
                <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/>
                <polyline points="9 22 9 12 15 12 15 22"/>
            </svg>
        ) : 'Overview'}
    </button>
    <button onClick={() => handleTabClick('mice')} className={activeView === 'mice' ? 'active' : ''} data-testid="mice-nav-button">
        {isMobile ? 'Mice' : 'Mice'}
    </button>
    <button onClick={() => handleTabClick('cages')} className={activeView === 'cages' ? 'active' : ''} data-testid="cages-nav-button">
        {isMobile ? 'Cages' : 'Cages'}
    </button>
    <button onClick={() => handleTabClick('litters')} className={activeView === 'litters' ? 'active' : ''} data-testid="litters-nav-button">
        {isMobile ? 'Litters' : 'Litters'}
    </button>

    {!isMobile && (
        <>
            <button onClick={() => setActiveView('genotype')} className={activeView === 'genotype' ? 'active' : ''}>
                Genotypes
            </button>
            <button onClick={() => setActiveView('settings')} className={activeView === 'settings' ? 'active' : ''}>
                Settings
            </button>
        </>
    )}
  </nav>
 );
}

// Hamburger menu for mobile — rendered in the header
function HamburgerMenu({ activeView, setActiveView, user, logout }) {
    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const menuRef = useRef(null);

    // Close menu when tapping outside
    useEffect(() => {
        if (!isMenuOpen) return;
        const handleOutsideClick = (e) => {
            if (menuRef.current && !menuRef.current.contains(e.target)) {
                setIsMenuOpen(false);
            }
        };
        document.addEventListener('pointerdown', handleOutsideClick);
        return () => document.removeEventListener('pointerdown', handleOutsideClick);
    }, [isMenuOpen]);

    return (
        <div className="hamburger-menu" ref={menuRef}>
            <button onClick={(e) => { setIsMenuOpen(!isMenuOpen); e.currentTarget.blur(); }} className="hamburger-icon">
                <span />
                <span />
                <span />
            </button>
            {isMenuOpen && (
                <div className="dropdown-menu">
                    <div className="dropdown-user-info">
                        Hi, {user?.first_name || user?.username}!
                    </div>
                    <button onClick={() => { setActiveView('genotype'); setIsMenuOpen(false); }} className={activeView === 'genotype' ? 'active' : ''}>
                        Genotypes
                    </button>
                    <button onClick={() => { setActiveView('settings'); setIsMenuOpen(false); }} className={activeView === 'settings' ? 'active' : ''}>
                        Settings
                    </button>
                    <button onClick={logout} className="dropdown-logout">
                        Logout
                    </button>
                </div>
            )}
        </div>
    );
}

function AppContent() {
 const { user, token, loading, logout, authError, clearAuthError } = useAuth();
 const [activeView, setActiveView] = useState('dashboard');
 const [cageFilter, setCageFilter] = useState('');
 const [initialSettingsSection, setInitialSettingsSection] = useState('general');
 const [showWelcome, setShowWelcome] = useState(StorageService.get('showWelcome') !== 'false');
 const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
 // Track which tabs have ever been visited so we only mount them once needed.
 const [mountedViews, setMountedViews] = useState(() => new Set(['dashboard']));
 const [failedSyncCount, setFailedSyncCount] = useState(0);
 const [retryingFailedSync, setRetryingFailedSync] = useState(false);
 const toast = useToast();
 const [isOffline, setIsOffline] = useState(!navigator.onLine);

 useSwipeNavigation(activeView, setActiveView);
 useKeyboardScroll();

 useEffect(() => {
   setMountedViews(prev => {
     if (prev.has(activeView)) return prev;
     const next = new Set(prev);
     next.add(activeView);
     return next;
   });
 }, [activeView]);

 // Save/restore independent scroll positions per tab
 const scrollPositions = useRef({});
 const prevView = useRef(activeView);
 useEffect(() => {
   const wrapper = document.querySelector('.swipe-content-wrapper');
   if (!wrapper) return;
   // Save scroll position of the tab we're leaving
   if (prevView.current !== activeView) {
     scrollPositions.current[prevView.current] = wrapper.scrollTop;
     // Restore scroll position of the tab we're entering
     requestAnimationFrame(() => {
       wrapper.scrollTop = scrollPositions.current[activeView] || 0;
     });
     prevView.current = activeView;
   }
 }, [activeView]);

 useEffect(() => {
    const handleResize = () => {
        setIsMobile(window.innerWidth <= 768);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
}, []);

 useEffect(() => {
    const goOffline = () => setIsOffline(true);
    const goOnline = () => {
        setIsOffline(false);
        // Push queued offline changes and pull server updates as soon as we reconnect.
        // DataService.sync() dispatches 'squeakbase-sync-complete' on success,
        // which causes all mounted management components to re-fetch their data.
        if (token) DataService.sync();
    };
    window.addEventListener('offline', goOffline);
    window.addEventListener('online',  goOnline);
    return () => {
        window.removeEventListener('offline', goOffline);
        window.removeEventListener('online',  goOnline);
    };
 }, [token]);

 // Background sync — pauses when the tab is hidden or the user has been idle.
 useIdleSync(token);

 // Hide the splash screen once auth check completes.
 // Without this, Capacitor auto-hides after a timeout (produces a warning in logs).
 // Also re-read showWelcome here: on iOS, StorageService.restore() runs async before
 // loading clears, so localStorage may have been empty at useState init time.
 useEffect(() => {
    if (!loading) {
        SplashScreen.hide().catch(() => {});
        setShowWelcome(StorageService.get('showWelcome') !== 'false');
    }
 }, [loading]);

 // Check for failed sync items periodically, and quietly re-attempt them (C13).
 //
 // Failures usually arrive in batches — one lost connection during a push fails
 // everything queued behind it — and the overwhelmingly common cause is
 // transient. Previously nothing retried on its own: the user had to notice the
 // banner, open Settings, and click retry per row, so a batch of failures from a
 // brief dropout sat there indefinitely.
 //
 // The auto-retry is deliberately much slower than the 30 s count poll: an item
 // only reaches failedSync after MAX_SYNC_RETRIES, so it has already failed
 // repeatedly and hammering it helps nobody.
 useEffect(() => {
    let lastAutoRetry = 0;
    const AUTO_RETRY_INTERVAL_MS = 5 * 60 * 1000;

    const checkFailedSync = async () => {
        try {
            const items = await DataService.getFailedSyncItems();
            setFailedSyncCount(items.length);

            if (
                items.length > 0 &&
                navigator.onLine &&
                Date.now() - lastAutoRetry > AUTO_RETRY_INTERVAL_MS
            ) {
                lastAutoRetry = Date.now();
                await DataService.retryAllFailedSyncItems();
                const remaining = await DataService.getFailedSyncItems();
                setFailedSyncCount(remaining.length);
            }
        } catch { /* ignore */ }
    };
    checkFailedSync();
    const interval = setInterval(checkFailedSync, 30000);
    return () => clearInterval(interval);
 }, []);

 // "Retry all" from the banner (C13).
 const handleRetryAllFailedSync = async () => {
    setRetryingFailedSync(true);
    try {
        const { requeued } = await DataService.retryAllFailedSyncItems();
        const remaining = await DataService.getFailedSyncItems();
        setFailedSyncCount(remaining.length);

        if (remaining.length === 0) {
            toast.success(`${requeued} change(s) synced.`);
        } else {
            // Say what actually happened rather than claiming success: some of
            // these fail for reasons a retry will never fix (a deleted cage, a
            // duplicate tag), and those need the per-item Review screen.
            toast.warn(`${remaining.length} change(s) still failing — open Review for details.`);
        }
    } catch (err) {
        toast.error('Could not retry the failed changes.');
    } finally {
        setRetryingFailedSync(false);
    }
 };


 const handleDismissWelcome = () => {
    StorageService.set('showWelcome', 'false');
    setShowWelcome(false);
  };

  const handleCloseWelcome = () => {
    setShowWelcome(false);
  }

  const navigateToSettings = (section = 'general') => {
    setInitialSettingsSection(section);
    setActiveView('settings');
  };

 if (loading) {
  return (
    <div className="app-loading" role="status" aria-label="Loading SqueakBase">
      <h1 className="app-loading-logo">SqueakBase</h1>
      <div className="app-loading-spinner" />
    </div>
  );
 }

  if (!user) {
    // Web shows marketing, native shows the sign-in form — the app binary has
    // no landing page to show.
    const publicHome = Capacitor.isNativePlatform()
      ? <Login />
      : <Suspense fallback={null}><LandingPage /></Suspense>;

    return (
     <Routes>
      <Route path="/" element={publicHome} />
      <Route path="/login" element={<Login />} />
      <Route path="/register" element={<Register onBack={() => window.location.href = '/login'} />} />
      <Route path="/forgot-password" element={<ForgotPassword onBack={() => window.location.href = '/login'} />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/verify-email" element={<VerifyEmail />} />

      {/* These are not routes the app navigates to — signed in, the UI is tab
          state with no router at all. They exist because the reminder emails
          link to them (backend/routes/cron.js builds `${FRONTEND_URL}/mice`,
          `/cages`, `/litters`), so they are live URLs in people's inboxes and
          must not start 404ing. Signed out they render what they always have;
          they are only marked noindex, because a marketing page served at
          three extra URLs is three duplicates of the homepage. */}
      {EMAIL_DEEP_LINK_PATHS.map((path) => (
        <Route key={path} path={path} element={<NoIndex>{publicHome}</NoIndex>} />
      ))}

      <Route path="*" element={Capacitor.isNativePlatform() ? <Login /> : <NotFound />} />
     </Routes>
    );
   }

  return (
    <div className="App">
      {authError && (
        <div className="auth-error-bar">
          <span>{authError}</span>
          <button onClick={clearAuthError}>X</button>
        </div>
      )}
       {showWelcome && <Welcome onDismiss={handleDismissWelcome} onClose={handleCloseWelcome} />}
      <header className="App-header">
        <div className="header-info">
          <h1 className="logo" style={{ fontFamily: "Alata, sans-serif", fontWeight: "600"}}>SqueakBase</h1>
          {isMobile ? (
            <HamburgerMenu activeView={activeView} setActiveView={setActiveView} user={user} logout={logout} />
          ) : (
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <span>Hi, {user.first_name || user.username}!</span>
              <button onClick={logout}>Logout</button>
            </div>
          )}
        </div>
      </header>

      <div className="swipe-content-wrapper">
        {isOffline && (
          <div className="offline-banner" role="alert" aria-live="polite">
            You are offline — changes will sync automatically when reconnected.
          </div>
        )}

        {failedSyncCount > 0 && (
          <div className="sync-failed-banner">
            <span>{failedSyncCount} change(s) failed to sync to the server.</span>
            <span className="sync-failed-banner-actions">
              <button
                onClick={handleRetryAllFailedSync}
                disabled={retryingFailedSync || isOffline}
              >
                {retryingFailedSync ? 'Retrying…' : 'Retry all'}
              </button>
              <button onClick={() => { setInitialSettingsSection('sync'); setActiveView('settings'); }}>Review</button>
            </span>
          </div>
        )}

        <div className={`swipe-panel${activeView === 'dashboard' ? ' swipe-panel-active' : ''}`} data-swipe-view="dashboard">
          {mountedViews.has('dashboard') && <Dashboard onNavigate={(view, opts) => { setActiveView(view); if (view === 'cages' && opts?.search) setCageFilter(opts.search); }} activeView={activeView} />}
        </div>
        <div className={`swipe-panel${activeView === 'mice' ? ' swipe-panel-active' : ''}`} data-swipe-view="mice">
          {mountedViews.has('mice') && <MiceManagement navigateToSettings={navigateToSettings} activeView={activeView} />}
        </div>
        <div className={`swipe-panel${activeView === 'cages' ? ' swipe-panel-active' : ''}`} data-swipe-view="cages">
          {mountedViews.has('cages') && <CageManagement activeView={activeView} initialSearch={cageFilter} onClearInitialSearch={() => setCageFilter('')} />}
        </div>
        <div className={`swipe-panel${activeView === 'litters' ? ' swipe-panel-active' : ''}`} data-swipe-view="litters">
          {mountedViews.has('litters') && <LitterManagement activeView={activeView} />}
        </div>
        {activeView === 'genotype' && <GenotypeManagement />}
        {activeView === 'settings' && <SettingsManagement initialSection={initialSettingsSection} />}
      </div>

      <Navigation activeView={activeView} setActiveView={setActiveView} isMobile={isMobile} />
      {!isMobile && <FeedbackButton />}
    </div>
  );
}

// Toasts when the refresh token is rejected (expired, or revoked by a logout /
// password change on another device). useAuth dispatches the window event
// because it renders inside this component's provider.
function SessionExpiredToast() {
  const toast = useToast();
  useEffect(() => {
    const handler = () => toast.error('Session expired — please sign in again');
    window.addEventListener('squeakbase-session-expired', handler);
    return () => window.removeEventListener('squeakbase-session-expired', handler);
  }, []);
  return null;
}

function App() {
 return (
  <ToastProvider>
   <SessionExpiredToast />
   <AuthProvider>
    <BrowserRouter>
     <AppContent />
    </BrowserRouter>
   </AuthProvider>
  </ToastProvider>
 );
}

export default App;
